import { VoxcoEventTypes, VoxcoEvent } from "./events";
import { StoreState } from "../models/states/StoreState";
import { __evaluateSafe__ } from "../expressions/parser";
import { Log } from "../utils/logging";
import { ValidationOptionsModel } from "../models/ValidationOptionsModel";
import { MentionBuilderDelegate } from "../models/builders/MentionBuilder";
import { DispositionCode } from "../models/enums/DispositionCode";
import { Piping } from "../piping";
import * as ACTIONS from "../redux/actions";
import { createRespondent } from "../models/factories/respondentFactory";
import { createQuestionnaire, createTheme } from "../models/factories/questionnaireFactory";
import { ThemeModel } from "../models/ThemeModel";
import { DispatchType, StoreType } from "../redux/actions/types";
import { BranchToOptions } from "../models/BranchToOptions";
import { convertToSurveyMode } from "../models/enums/SurveyMode";
import { getRandomInt } from "../randomization/Randomizer";
import { applyCSS, generateCSS } from "../utils/dynamic-css/coreDynamicCss";
import { addVoxcoAgentListener } from "../utils/voxcoAgentHelper";

/** @hidden */
export type VoxcoEventListener<E extends VoxcoEvent> = (this: PublicApi, ev: E) => void;

/** @hidden */
export interface VoxcoEventListeners {
    [event: string]: VoxcoEventListener<any>[];
}

/** @hidden */
export interface VoxcoConfig {
    debug: boolean;
    mode: "test" | "preview" | "normal" | "simulation";
    displayInThemeEditor: boolean;
    messages: boolean;
    mobileMode: boolean;
    apiUrl?: string;
    survey?: any;
    containerHost?: string;
    parent?: string;
    previewQuestionId?: number;
    hideAccessibilityPanel: boolean;
}

const tricklingListeners: VoxcoEventListeners = {};
const capturingListeners: VoxcoEventListeners = {};

const configDefaults: VoxcoConfig = {
    debug: false,
    mode: "normal",
    messages: false,
    mobileMode: false,
    displayInThemeEditor: false,
    hideAccessibilityPanel: false,
};

const simulatedRespondent = createRespondent({
    respondentId: 0,
    simulated: true,
    isNew: true,
    seed: getRandomInt(),
    sessionStartTime: 0,
    offlineUserId: 0,
    offlineUserName: "",
    resSelectedSecurityQuestionId: 0,
    IsResLocked: false,
});

const simulatedDevice = {
    model: "simulated",
    browser: "simulated",
    os: "simulated",
    version: "simulated",
    type: "simulated",
};

function getListeners<K extends keyof VoxcoEventTypes>(
    event: K,
    capture?: boolean,
): VoxcoEventListener<any>[] {
    const listeners = capture ? capturingListeners : tricklingListeners;
    if (!listeners[event]) {
        listeners[event] = [];
    }
    return listeners[event];
}

function createVoxcoEvent(store: StoreType, e: VoxcoEvent): VoxcoEvent {
    const result = {
        ...e,
        interview: store.getState().interview,
        defaultPrevented: false,
        stoppedPropagation: false,
        stoppedImmediatePropagation: false,
    };
    result.preventDefault = () => {
        result.defaultPrevented = true;
    };
    result.stopPropagation = () => {
        result.stoppedPropagation = true;
    };
    result.stopImmediatePropagation = () => {
        result.stoppedImmediatePropagation = true;
    };
    return result;
}

export class PublicApi {
    /** @hidden */
    static TAG = "publicApi";

    /** @hidden */
    version = require("../../package.json").version;
    /** @hidden */
    lib = process.env.LIB_NAME;
    /** @hidden */
    store: StoreType = undefined;
    /** @hidden */
    dispatch: DispatchType = undefined;

    /** @hidden */
    // eslint-disable-next-line no-useless-constructor, @typescript-eslint/no-empty-function
    constructor() {}

    /** @hidden */
    async init(config: VoxcoConfig = configDefaults) {
        const {
            mode,
            debug,
            messages,
            apiUrl,
            survey,
            containerHost,
            parent,
            previewQuestionId,
            hideAccessibilityPanel,
        } = config;
        this.dispatch(ACTIONS.setPageMessagesEnabled(messages));
        if (debug) {
            this.dispatch(ACTIONS.debugEnabled());
        }

        this.dispatch(ACTIONS.setHideAccessibilityPanel(hideAccessibilityPanel));
        this.dispatch(ACTIONS.setSurveyMode(convertToSurveyMode(mode)));

        // Convert the displayInThemeEditor flag to boolean before setting it
        // This is because the parameter sent from the backend would always get stored as string
        const displayInThemeEditor =
            config.displayInThemeEditor &&
            config.displayInThemeEditor.toString().toLowerCase() === "true";
        this.dispatch(ACTIONS.setDisplayInThemeEditor(displayInThemeEditor));

        if (apiUrl !== undefined) {
            this.dispatch(ACTIONS.connectionStart(apiUrl));
        } else if (survey !== undefined) {
            this.dispatch(ACTIONS.respondentChanged(simulatedRespondent));
            this.dispatch(ACTIONS.deviceChanged(simulatedDevice));
            this.dispatch(ACTIONS.questionnaireChanged(createQuestionnaire(survey)));
            this.dispatch(ACTIONS.connectionConnected("simulated"));
        }

        if (previewQuestionId && previewQuestionId > -1) {
            Voxco.once("pageReady", () =>
                this.dispatch(ACTIONS.navigationPreviewQuestion(previewQuestionId)),
            );
        }

        // Alert host window when we are ready or quitting.
        if (containerHost && window.parent !== window) {
            window.parent.postMessage({ type: "ready" }, containerHost);

            Voxco.on("quit", ({ dispositionCode, options }) => {
                window.parent.postMessage(
                    {
                        type: "quit",
                        dispositionCode,
                        options,
                    },
                    containerHost,
                );
            });
        }

        // Listen for page messages.
        window.addEventListener("message", (event) => {
            const { origin, data } = event;
            if (containerHost !== origin) {
                return;
            }
            switch (data.type) {
                case "previewTheme": {
                    const theme = createTheme(data.theme);
                    if (data.isDynamicCssChanged && theme.settings?.DynamicCss) {
                        const css = generateCSS(theme.settings, theme.settings.DynamicCss);
                        applyCSS(css);
                    } else {
                        this.previewTheme(theme);
                    }
                    break;
                }
                case "quit": {
                    this.quit(data.dispositionCode, data.options);
                    break;
                }
                default:
                    break;
            }
        });

        this.dispatch(ACTIONS.setParent(parent));
        addVoxcoAgentListener(parent);
    }

    /**
     * @hidden
     */
    log = (message?: any) => Log.d(PublicApi.TAG, "LOG", message);

    /**
     * @hidden
     */
    error = (error?: any) =>
        Log.e(PublicApi.TAG, "ERROR", "An error has occurred in custom.js.", error);

    /**
     * Used to attach your custom script to an event.
     * #### Example
     *  ```typescript
     * Voxco.on("pageReady", function(ev) {
     *      alert("Hello world!")
     *      return ev;
     *  })
     * ```
     * #### See Also
     * {@link off}
     * {@link once}
     * @param event See [events](../interfaces/events.VoxcoEventTypes.html).
     * @param listener Called on event.
     * @param capture
     */
    on<K extends keyof VoxcoEventTypes>(
        event: K,
        listener: VoxcoEventListener<VoxcoEventTypes[K]>,
        capture?: boolean,
    ) {
        const listeners = getListeners(event, capture);
        listeners.push(listener);
        if (event === "pageReady") {
            // If the page is ready, call right away.
            const store: StoreState = this.store.getState();
            if (
                store.interview.navigation.currentPage !== undefined &&
                store.interview.navigation.currentPage.isReady
            ) {
                listener.apply(this, [store.interview.navigation.currentPage]);
            }
        }
    }

    /**
     * Used to unattach your custom script from an event.
     * #### Example
     *  ```typescript
     * //
     * function listener(ev) {
     *      alert("Hello world!");
     * }
     * Voxco.on("pageReady", listener);
     * // ...
     * Voxco.off("pageReady", listener);
     * ```
     * #### See Also
     * {@link on}
     * {@link once}
     * @param event See [events](../interfaces/events.VoxcoEventTypes.html).
     * @param listener Script to unattach.
     * @param capture
     */
    off<K extends keyof VoxcoEventTypes>(
        event: K,
        listener: VoxcoEventListener<VoxcoEventTypes[K]>,
        capture?: boolean,
    ) {
        const listeners = getListeners(event, capture);
        const index = listeners.indexOf(listener);
        if (index > -1) listeners.splice(index, 1);
    }

    /**
     * Attach your custom script for to a single instance of an event.
     * #### Example
     *  ```typescript
     * Voxco.once("pageReady", function(ev) {
     *      alert("Hello world!")
     *      return ev;
     *  })
     * ```
     * #### See Also
     * {@link on}
     * {@link off}
     * @param event See [events](../interfaces/events.VoxcoEventTypes.html).
     * @param listener Called on event.
     * @param capture
     */
    once<K extends keyof VoxcoEventTypes>(
        event: K,
        listener: VoxcoEventListener<VoxcoEventTypes[K]>,
        capture?: boolean,
    ) {
        const api = this;
        let done = false;
        const listenerWrapper = (ev: VoxcoEventTypes[K]) => {
            if (done) return;
            done = true;
            const result = listener.apply(api, [ev]);
            this.off(event, listenerWrapper, capture);
            return result;
        };
        this.on(event, listenerWrapper, capture);
    }

    /**
     * Set a variable's value.
     * #### Example
     * ```typescript
     * // String mention example
     * Voxco.setVariableMentions("NewValue", "Q1", 1)
     * // String[] mention example
     * Voxco.setVariableMentions(["First mention value", "Second mention value"], "Q1", 1)
     * // Mention builder example
     * Voxco.setVariableMentions((mentions) => mentions.clear().add("New value"), "Q1", 1)
     * ```
     * #### See Also
     * None
     * @param mentions New variable value.
     * @param variableName Variable being modified.
     * @param matrixIndex Uses the current matrix index if not set.
     *
     */
    setVariableMentions = (
        mentions: MentionBuilderDelegate | string | string[],
        variableName: string,
        matrixIndex?: number,
    ) => {
        if (typeof mentions === "function")
            return this.trigger("setVariableMentions", {
                builder: mentions,
                variableName,
                matrixIndex,
            });
        return this.trigger("setVariableMentions", {
            builder: (m) => m.clear().add(mentions as any),
            variableName,
            matrixIndex,
        });
    };

    /** @hidden */
    trigger<K extends keyof VoxcoEventTypes>(event: K, data?: VoxcoEventTypes[K]): any {
        let evt = createVoxcoEvent(this.store, data);

        const capturingListeners = getListeners(event, true);
        for (const listener of capturingListeners) {
            try {
                evt = listener.apply(this, [evt]) || evt;
            } catch (ex) {
                this.error(ex);
            }
            if (evt.stoppedPropagation) {
                return evt;
            }
            if (evt.stoppedImmediatePropagation) {
                break;
            }
            evt = { ...evt, stoppedImmediatePropagation: false };
        }

        const listeners = [...getListeners(event)];

        let listener;
        for (let i = 0; i < listeners.length; i++) {
            listener = listeners[i];
            try {
                evt = listener.apply(this, [evt]) || evt;
                if (event === "questionReady") {
                    this.off(event, listener);
                }
            } catch (ex) {
                this.error(ex);
            }
            if (evt.stoppedPropagation) {
                return evt;
            }
            if (evt.stoppedImmediatePropagation) {
                break;
            }
            evt = { ...evt, stoppedImmediatePropagation: false };
        }

        return evt;
    }

    /** @hidden */
    previewTheme(theme: ThemeModel) {
        this.dispatch(
            ACTIONS.previewTheme({
                ...theme,
                settings: {
                    ...theme.settings,
                    CustomJavascript: undefined,
                },
            }),
        );
    }

    /**
     * Evaluate an advanced expression.
     * #### Example
     * ```typescript
     * Voxco.setVariableMentions("1", "Q1")
     * Voxco.evaluate("Q1=1").asBoolean()
     * // returns true
     * ```
     * #### See Also
     * None
     * @param expression See {@link https://help.voxco.com/voxco-online-v70-en/Default.htm#voxco-online/surveys/edit/questionnaire/questionnaire-logic/advanced-expressions.htm | Advanced expression}.
     * @param simple Is the expression simple, otherwise advanced.
     * @returns The expression's value.
     */
    evaluate = (expression: string, simple?: boolean) =>
        __evaluateSafe__(this.store.getState().interview, expression, simple);

    /**
     * Recalls a variable using {@link https://help.voxco.com/voxco-online-v70-en/Default.htm#voxco-online/surveys/edit/questionnaire/questionnaire-logic/piping.htm | piping}.
     * #### Example
     * ```typescript
     * Voxco.setVariableMentions("1", "Q1")
     * Voxco.recall("[Q1]")
     * // returns "1"
     * ```
     * #### See Also
     * None
     * @param text Variable to recall.
     * @returns The variable's value
     */
    recall = (text: string) => Piping.evaluateRecalls(this.store.getState().interview, text);

    /** @hidden */
    showDebugInfo = () => this.dispatch(ACTIONS.debugInfoEnabled());

    /**
     * Validates the page.
     * #### Example
     * ```typescript
     * var options = new ValidationOptionsModel();
     * options.silent = true;
     * Voxco.validatePage(options);
     * ```
     * #### See Also
     * None
     * @param options Set the silent variable to hide the errors on the page.
     */
    validatePage = (options?: ValidationOptionsModel) => this.trigger("validatePage", { options });

    /**
     * Move to a question.
     * #### Example
     * ```typescript
     * var options = new BranchToOptions();
     * options.destination = "Q2";
     * Voxco.branchTo(options);
     * ```
     * #### See Also
     * None
     * @param options Set the destination.
     */
    branchTo = (options?: BranchToOptions) => this.trigger("branchTo", options);

    /**
     * Display a termination message.
     * #### Example
     * ```typescript
     * Voxco.displayMessage("Termination message");
     * ```
     * #### See Also
     * None
     */
    displayMessage = (message: string) => this.trigger("displayMessage", { message });

    /**
     * Navigates to the next page.
     * #### Example
     * ```typescript
     * Voxco.nextPage();
     * ```
     * #### See Also
     * {@link previousPage}
     * {@link quit}
     */
    nextPage = () => this.trigger("nextPage");

    /**
     * Navigates to the previous page.
     * #### Example
     * ```typescript
     * Voxco.previousPage();
     * ```
     * #### See Also
     * {@link nextPage} {@link quit}
     */
    previousPage = () => this.trigger("previousPage");

    /**
     * Quits the survey and closes the respondent session.
     * #### Example
     * ```typescript
     * Voxco.quit();
     * ```
     * #### See Also
     * {@link nextPage} {@link previousPage}
     */
    quit = (
        dispositionCode?: DispositionCode,
        options?: {
            message?: string;
            closedStrata?: boolean;
            redirectUrl?: string;
        },
    ) => this.trigger("quit", { dispositionCode, options });

    /**
     * #### See Also
     * {@link on}
     */
    onPageReady = (listener: VoxcoEventListener<VoxcoEventTypes["pageReady"]>) =>
        this.on("pageReady", listener);

    /**
     * #### See Also
     * {@link on}
     */
    onQuestionReady = (listener: VoxcoEventListener<VoxcoEventTypes["questionReady"]>) => {
        this.on("questionReady", listener);
    };

    /**
     * #### See Also
     * {@link on}
     */
    onValidatePage = (listener: VoxcoEventListener<VoxcoEventTypes["validatePage"]>) =>
        this.on("validatePage", listener);

    /**
     * #### See Also
     * {@link on}
     */
    onSetVariableMentions = (
        listener: VoxcoEventListener<VoxcoEventTypes["setVariableMentions"]>,
    ) => this.on("setVariableMentions", listener);

    /**
     * #### See Also
     * {@link on}
     */
    onBranchTo = (listener: VoxcoEventListener<VoxcoEventTypes["branchTo"]>) =>
        this.on("branchTo", listener);

    /**
     * #### See Also
     * {@link on}
     */
    onNextPage = (listener: VoxcoEventListener<VoxcoEventTypes["nextPage"]>) =>
        this.on("nextPage", listener);

    /**
     * #### See Also
     * {@link on}
     */
    onPreviousPage = (listener: VoxcoEventListener<VoxcoEventTypes["previousPage"]>) =>
        this.on("previousPage", listener);

    /**
     * #### See Also
     * {@link on}
     */
    onQuit = (listener: VoxcoEventListener<VoxcoEventTypes["quit"]>) => this.on("quit", listener);

    /**
     * Set the respondent's appointment date.
     * #### Example
     * ```typescript
     * var nextDate = new Date();
     * nextDate.setDate(nextDate.getDate() + 1);
     * Voxco.setAppointment(nextDate);
     * ```
     * #### See Also
     * {@link setEmail} {@link setPhone} {@link setTimeZone} {@link setLoopRow}
     */
    setAppointment = (appointment: Date) => this.trigger("setAppointment", { appointment });

    /**
     * Set the respondent's email.
     * #### Example
     * ```typescript
     * Voxco.setEmail("test@abc.com");
     * ```
     * #### See Also
     * {@link setAppointment} {@link setPhone} {@link setTimeZone} {@link setLoopRow}
     */
    setEmail = (email: string) => this.trigger("setEmail", { email });

    /**
     * Set the respondent's phone number.
     * #### Example
     * ```typescript
     * Voxco.setPhone("5555551234");
     * ```
     * #### See Also
     * {@link setAppointment} {@link setEmail} {@link setTimeZone} {@link setLoopRow}
     */
    setPhone = (phone: string) => this.trigger("setPhone", { phone });

    /**
     * Set the respondent's time zone.
     * #### Example
     * ```typescript
     * Voxco.setTimeZone("1");
     * ```
     * #### See Also
     * {@link setAppointment} {@link setEmail} {@link setPhone} {@link setLoopRow}
     * @param timeZone UTC time.
     */
    setTimeZone = (timeZone: string) => this.trigger("setTimeZone", { timeZone });

    /**
     * Set the current matrix index for the given block.
     * #### Example
     * ```typescript
     * Voxco.setLoopRow("block1", 2);
     * ```
     * #### See Also
     * {@link setAppointment} {@link setEmail} {@link setPhone} {@link setTimeZone}
     * @param blockName
     * @param row Matrix index.
     */
    setLoopRow = (blockName: string, row: number) => this.trigger("setLoopRow", { blockName, row });
}

/** Exposed API used by your custom script */
export const Voxco = new PublicApi();
